import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import type { Ailment, LateralLocation } from "api/schemas/shared/Treatment";
import type { HealthFormType } from "api/schemas/UserCourse";
import type { Joint } from "api/types/GetActivityLibraryEntriesArgs";

interface UseHealthformWarningProps {
  healthFormType: HealthFormType | undefined;
  patient: {
    ailment?: Ailment;
    most_painful_location?: {
      location?: Joint;
      lateral_location?: LateralLocation;
    };
    awaiting_treatment_ready?: boolean;
  };
  hasActivatedTreatment: boolean;
}

export const useHealthformWarning = ({ healthFormType, patient, hasActivatedTreatment }: UseHealthformWarningProps) => {
  const { t } = useTranslation();
  const [healthFormTypeMismatchWarning, setHealthFormTypeMismatchWarning] = useState<string>("");

  useEffect(() => {
    const { ailment, most_painful_location } = patient;

    if (!(most_painful_location?.location || ailment) || healthFormType === undefined) {
      return;
    }

    const { location, lateral_location } = patient.most_painful_location || {};

    if (!hasActivatedTreatment) {
      const healthFormJointTypeMap: { [key: string]: Joint[] } = {
        neck: ["neck"],
        hand_oa: ["hand"],
        hip_oa: ["hip"],
        knee_oa: ["knee"],
        low_back_pain: ["lower_back"],
        shoulder_pain: ["shoulder"],
      };

      const healthFormAilmentTypeMap: { [key: string]: Ailment[] } = {
        osteoporosis: ["osteoporosis", "fall_prevention"],
        fall_prevention: ["fall_prevention", "osteoporosis"],
      };

      const jointMatch = location && healthFormJointTypeMap[healthFormType]?.includes(location);
      const ailmentMatch = ailment && healthFormAilmentTypeMap[healthFormType]?.includes(ailment);

      if (location && !jointMatch && healthFormType !== "generic") {
        const painLocation = `$${
          lateral_location && lateral_location !== "undefined"
            ? `${t(`patients.lateral_location.${lateral_location}`)} `
            : ""
          // FIXME: type translation
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
        }${t(`patients.pain_location.${location}.0`)}`;

        const hft = t(`usercourse.healthformtype.${healthFormType as HealthFormType}`);

        const message = t("usercourse.templates.HFTMismatchJoint", { hft, painLocation });

        setHealthFormTypeMismatchWarning(message);
      } else if (!location && ailment && !ailmentMatch) {
        const translatedAilment = t(`patients.ailments.${ailment}`);

        const hft = t(`usercourse.healthformtype.${healthFormType as HealthFormType}`);

        const message = t("usercourse.templates.HFTMismatchAilment", { hft, ailment: translatedAilment });

        setHealthFormTypeMismatchWarning(message);
      } else {
        setHealthFormTypeMismatchWarning("");
      }
    }
  }, [healthFormType, patient, hasActivatedTreatment, t]);

  return healthFormTypeMismatchWarning;
};
